import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import Modal from "../../../components/modal/Modal";
import { MaintenanceLog } from "../../../types/api";
import { getAssetInventory } from "../../../service/api";
import { useOrgUsersStore } from "../../../store/store";
import { usePreventiveMaintenanceStore } from "../../../store/utilitySystemsStore";

interface MaintenanceLogFormProps {
  isOpen: boolean;
  onClose: () => void;
  log?: MaintenanceLog | null;
  mode: "create" | "edit" | "view";
  onSuccess?: () => void;
}

interface PartUsed {
  name: string;
  quantity: number;
  cost?: number;
}

const MaintenanceLogForm: React.FC<MaintenanceLogFormProps> = ({
  isOpen,
  onClose,
  log,
  mode,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    assetId: "",
    taskId: "",
    technicianId: "",
    workDone: "",
    partsUsedJson: [] as PartUsed[],
    timeTaken: "",
    timeTakenUnit: "hours" as "minutes" | "hours",
    cost: "",
    remarks: "",
  });
  const [assets, setAssets] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const {
    logsLoading,
    maintenanceTasks,
    fetchMaintenanceLogById,
    createMaintenanceLog: createLog,
    updateMaintenanceLog: updateLog,
    fetchMaintenanceTasks,
  } = usePreventiveMaintenanceStore();

  const isDisabled = mode === "view";
  const isReadOnly = mode === "view";

  const fetchAssets = useCallback(async () => {
    try {
      const response = await getAssetInventory();
      if (response.data?.data) {
        setAssets(Array.isArray(response.data.data) ? response.data.data : []);
      } else if (response.data?.message) {
        setAssets(Array.isArray(response.data.message) ? response.data.message : []);
      }
    } catch (error) {
      console.error("Error fetching asset inventory:", error);
    }
  }, []);

  const fetchTasks = useCallback(async () => {
    try {
      await fetchMaintenanceTasks();
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  }, [fetchMaintenanceTasks]);

  const loadLogData = useCallback(async () => {
    if (!log) return;

    try {
      const id = log.id || log._id;
      if (!id) return;

      const logData = await fetchMaintenanceLogById(id) || log;

      setFormData({
        assetId: logData.assetId || "",
        taskId: logData.taskId || "",
        technicianId: logData.technicianId || "",
        workDone: logData.workDone || "",
        partsUsedJson: logData.partsUsedJson?.map((part) => ({ name: part.partName, quantity: part.quantity, cost: part.cost })) || [],
        timeTaken: logData.timeTaken?.toString() || "",
        timeTakenUnit: logData.timeTakenUnit || "hours",
        cost: logData.cost?.toString() || "",
        remarks: logData.remarks || "",
      });
    } catch (error) {
      console.error("Error loading log:", error);
      toast.error("Failed to load log data");
      // Fallback 
      if (log) {
        setFormData({
          assetId: log.assetId || "",
          taskId: log.taskId || "",
          technicianId: log.technicianId || "",
          workDone: log.workDone || "",
          partsUsedJson: log.partsUsedJson?.map((part) => ({ name: part.partName, quantity: part.quantity, cost: part.cost })) || [],
          timeTaken: log.timeTaken?.toString() || "",
          timeTakenUnit: log.timeTakenUnit || "hours",
          cost: log.cost?.toString() || "",
          remarks: log.remarks || "",
        });
      }
    }
  }, [log, fetchMaintenanceLogById]);

  useEffect(() => {
    if (!orgUsersData.length) fetchOrgUsers();
    fetchAssets();
    fetchTasks();
  }, [fetchAssets, fetchTasks]);

  useEffect(() => {
    if (log && mode !== "create") {
      loadLogData();
    } else {
      setFormData({
        assetId: "",
        taskId: "",
        technicianId: "",
        workDone: "",
        partsUsedJson: [],
        timeTaken: "",
        timeTakenUnit: "hours",
        cost: "",
        remarks: "",
      });
    }
  }, [log, mode, loadLogData]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.assetId) {
      newErrors.assetId = "Asset is required";
    }

    if (!formData.technicianId) {
      newErrors.technicianId = "Technician is required";
    }

    if (!formData.workDone.trim()) {
      newErrors.workDone = "Work Done description is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isReadOnly) return;

    if (!validate()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const submitData = {
        assetId: formData.assetId,
        taskId: formData.taskId || undefined,
        technicianId: formData.technicianId,
        workDone: formData.workDone.trim(),
        partsUsedJson: formData.partsUsedJson.map((part) => ({ partName: part.name, quantity: part.quantity, cost: part.cost })),
        timeTaken: formData.timeTaken ? Number(formData.timeTaken) : undefined,
        timeTakenUnit: formData.timeTakenUnit,
        cost: formData.cost ? Number(formData.cost) : undefined,
        remarks: formData.remarks.trim() || undefined,
      };

      if (mode === "create") {
        await createLog(submitData);
        toast.success("Maintenance log created successfully");
      } else if (mode === "edit" && log) {
        const id = log.id || log._id;
        if (!id) {
          toast.error("Log ID not found");
          return;
        }
        await updateLog(id, {
          ...submitData
        });
        toast.success("Maintenance log updated successfully");
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error: any) {
      toast.error(
        error.response?.data?.resMessage ||
          error.response?.data?.error ||
          error.message ||
          `Failed to ${mode === "create" ? "create" : "update"} maintenance log`
      );
    }
  };

  const handleAddPart = () => {
    setFormData({
      ...formData,
      partsUsedJson: [...formData.partsUsedJson, { name: "", quantity: 1, cost: 0 }],
    });
  };

  const handleRemovePart = (index: number) => {
    setFormData({
      ...formData,
      partsUsedJson: formData.partsUsedJson.filter((_, i) => i !== index),
    });
  };

  const handlePartChange = (index: number, field: keyof PartUsed, value: string | number) => {
    const updatedParts = [...formData.partsUsedJson];
    updatedParts[index] = {
      ...updatedParts[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      partsUsedJson: updatedParts,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeButton={false}
      modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
    >
      <div className="pt-6 px-6 flex-1 overflow-y-auto">
        <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
          {mode === "create" ? "Create Maintenance Log" : mode === "edit" ? "Edit Maintenance Log" : "View Maintenance Log"}
        </h2>
        <form onSubmit={handleSubmit} id="maintenanceLogForm" className="flex flex-col gap-6 pb-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Asset <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.assetId}
                onChange={(e) => setFormData({ ...formData, assetId: e.target.value })}
                disabled={isDisabled}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent ${
                  errors.assetId ? "border-red-500" : ""
                }`}
              >
                <option value="">Select Asset</option>
                {assets.map((asset) => (
                  <option key={asset._id || asset.id} value={asset._id || asset.id}>
                    {asset.assetName || asset.name || asset.assetCode || asset.code || asset._id || asset.id}
                  </option>
                ))}
              </select>
              {errors.assetId && <p className="text-red-500 text-sm mt-1">{errors.assetId}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Related Task (Optional)
              </label>
              <select
                value={formData.taskId}
                onChange={(e) => setFormData({ ...formData, taskId: e.target.value })}
                disabled={isDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              >
                <option value="">No Task</option>
                {tasks
                  .filter((task) => !formData.assetId || task.assetId === formData.assetId)
                  .map((task) => (
                    <option key={task._id || task.id} value={task._id || task.id}>
                      {`${task.assetName || task.assetId} - ${task.status || "N/A"}`}
                    </option>
                  ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Technician <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.technicianId}
                onChange={(e) => setFormData({ ...formData, technicianId: e.target.value })}
                disabled={isDisabled}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent ${
                  errors.technicianId ? "border-red-500" : ""
                }`}
              >
                <option value="">Select Technician</option>
                {orgUsersData.map((user: any) => (
                  <option key={user._id || user.id} value={user._id || user.id}>
                    {user.name || user.email || user._id || user.id}
                  </option>
                ))}
              </select>
              {errors.technicianId && (
                <p className="text-red-500 text-sm mt-1">{errors.technicianId}</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Time Taken
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  value={formData.timeTaken}
                  onChange={(e) => setFormData({ ...formData, timeTaken: e.target.value })}
                  placeholder="Time"
                  min="0"
                  step="0.1"
                  disabled={isDisabled}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
                />
                <select
                  value={formData.timeTakenUnit}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      timeTakenUnit: e.target.value as "minutes" | "hours",
                    })
                  }
                  disabled={isDisabled}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-gray-700">
                Total Cost
              </label>
              <input
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                placeholder="0.00"
                min="0"
                step="0.01"
                disabled={isDisabled}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Work Done <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.workDone}
              onChange={(e) => setFormData({ ...formData, workDone: e.target.value })}
              placeholder="Describe the work performed..."
              rows={4}
              disabled={isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
            />
            {errors.workDone && <p className="text-red-500 text-sm mt-1">{errors.workDone}</p>}
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Parts Used
              </label>
              {!isDisabled && (
                <button
                  type="button"
                  onClick={handleAddPart}
                  className="text-[#0fa9a6] hover:text-[#0d8f8c] flex items-center gap-1 text-sm"
                >
                  <Plus size={16} />
                  Add Part
                </button>
              )}
            </div>
            <div className="space-y-3">
              {formData.partsUsedJson.map((part, index) => (
                <div key={index} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-5">
                    <input
                      type="text"
                      value={part.name}
                      onChange={(e) => handlePartChange(index, "name", e.target.value)}
                      placeholder="Part Name"
                      disabled={isDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="number"
                      value={part.quantity}
                      onChange={(e) => handlePartChange(index, "quantity", Number(e.target.value))}
                      placeholder="Quantity"
                      min="1"
                      disabled={isDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
                    />
                  </div>
                  <div className="col-span-3">
                    <input
                      type="number"
                      value={part.cost || ""}
                      onChange={(e) => handlePartChange(index, "cost", Number(e.target.value))}
                      placeholder="Cost"
                      min="0"
                      step="0.01"
                      disabled={isDisabled}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
                    />
                  </div>
                  {!isDisabled && (
                    <div className="col-span-1">
                      <button
                        type="button"
                        onClick={() => handleRemovePart(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
              {formData.partsUsedJson.length === 0 && (
                <p className="text-gray-500 text-sm">No parts added</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Remarks
            </label>
            <textarea
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Additional notes or remarks..."
              rows={3}
              disabled={isDisabled}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0fa9a6] focus:border-transparent"
            />
          </div>

          <div className="h-20" />
        </form>
      </div>

      {!isReadOnly && (
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={onClose}
            disabled={logsLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="maintenanceLogForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={logsLoading}
          >
            {logsLoading ? "Saving..." : mode === "create" ? "Create" : "Update"}
          </button>
        </div>
      )}
    </Modal>
  );
};

export default MaintenanceLogForm;

